import { createApiClient } from './api/client.js';
import { extractShareTokenFromUrl } from './api/share-token.js';
import { initI18n } from './i18n/init.js';
import { createDeviceMatcher } from './lib/device.js';
import { isCrossOriginIsolated, readRuntimeEnv } from './lib/env.js';
import { isEmbedded } from './lib/iframe.js';
import { createOrchestrator } from './orchestrator/orchestrator.js';
import { createAppStore } from './state/store.js';
import {
  isCoachDismissed,
  markCoachDismissed,
  resetCoachDismissed,
} from './ui/coach-storage.js';
import { createRenderer } from './ui/render.js';
import { SparkViewerAdapter } from './viewer/spark-adapter.js';

function showIsolationOverlay(): void {
  const overlay = document.createElement('div');
  overlay.id = 'oniri-isolation-error';
  overlay.setAttribute('role', 'alert');
  overlay.style.cssText =
    'position:fixed;inset:0;background:#7f1d1d;color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;font-family:system-ui,sans-serif;text-align:center;padding:2rem;z-index:9999';
  overlay.innerHTML = `
    <h1 style="font-size:1.5rem;font-weight:600;margin-bottom:1rem">Impossibile aprire la scena</h1>
    <p style="max-width:32rem;line-height:1.5">Si è verificato un problema tecnico nel caricamento. Riprova più tardi o contatta ONIRI.</p>
    <button type="button" id="oniri-isolation-dismiss" style="margin-top:1.5rem;padding:0.5rem 1rem;background:#fff;color:#7f1d1d;border:0;border-radius:0.375rem;cursor:pointer;font-weight:500">Continua comunque</button>
  `;
  document.body.appendChild(overlay);
  document.getElementById('oniri-isolation-dismiss')?.addEventListener('click', () => {
    overlay.remove();
  });
}

interface Layers {
  canvas: HTMLCanvasElement;
  uiRoot: HTMLElement;
}

function ensureLayers(app: HTMLElement): Layers {
  app.innerHTML = '';

  const canvasLayer = document.createElement('div');
  canvasLayer.id = 'viewer-canvas-layer';
  canvasLayer.style.cssText = 'position:fixed;inset:0;z-index:0';

  const canvas = document.createElement('canvas');
  canvas.id = 'viewer-canvas';
  canvas.style.cssText = 'display:block;width:100vw;height:100vh';
  canvasLayer.appendChild(canvas);

  const uiRoot = document.createElement('div');
  uiRoot.id = 'viewer-ui-root';
  // pointer-events:none lets orbit drag pass through; CTAs re-enable via class.
  uiRoot.style.cssText = 'position:fixed;inset:0;z-index:10;pointer-events:none';

  app.appendChild(canvasLayer);
  app.appendChild(uiRoot);

  return { canvas, uiRoot };
}

async function bootstrap(): Promise<void> {
  const env = readRuntimeEnv('main');
  console.warn('[oniri-viewer] mode=main env=', env);

  if (!isCrossOriginIsolated()) {
    showIsolationOverlay();
  }

  if (isEmbedded(window.location.href)) {
    console.warn(
      '[oniri-viewer] / served inside iframe — frame-ancestors header should have blocked it; check deploy',
    );
  }

  const root = document.getElementById('app');
  if (!root) {
    console.error('[oniri-viewer] missing #app root');
    return;
  }

  // Phase 12 Inc1: inizializza i18n PRIMA del mount UI (mirror di studio.ts).
  // initI18n() fa il fetch del dizionario della lingua negoziata
  // (cookie > navigator.language > 'it') da /i18n/<locale>.json — un singolo
  // fetch same-origin di un .json ~5KB. DEVE completare prima di
  // renderer.start() altrimenti i componenti del viewer mostrerebbero le
  // chiavi i18n invece del testo. Best-effort: initI18n non lancia mai (fetch
  // failure → dizionario vuoto → fallback chiave), quindi nessun try/catch.
  await initI18n();

  const { canvas, uiRoot } = ensureLayers(root);
  const adapter = new SparkViewerAdapter();
  // Audit C3 fix 2026-06-03 (CRITICAL): share_token estratto da URL
  // (?share_token=... oppure ?share=...) e' propagato a tutte le chiamate
  // API model/hotspots/presets/floorplan. Senza, le chiamate sono
  // public-only e il backend ritorna 404 per modelli share_only.
  const extractedShareToken = extractShareTokenFromUrl(window.location.href);
  const apiClient = createApiClient(
    extractedShareToken !== null ? { shareToken: extractedShareToken } : {},
  );
  const store = createAppStore();
  // 4.M: device matcher owned by bootstrap. Re-broadcast through the
  // orchestrator's 'device-state' event keeps the renderer single-
  // channel; the body[data-pointer-coarse] attribute is set here so
  // tap-target CSS rules apply globally.
  const deviceMatcher = createDeviceMatcher();
  document.body.dataset['pointerCoarse'] = String(deviceMatcher.snapshot.pointerCoarse);
  const bodyDatasetUnsub = deviceMatcher.subscribe((next) => {
    document.body.dataset['pointerCoarse'] = String(next.pointerCoarse);
  });

  const orchestrator = createOrchestrator({
    apiClient,
    adapter,
    store,
    canvas,
    href: window.location.href,
    embed: false,
    deviceMatcher,
  });

  const renderer = createRenderer({
    uiRoot,
    store,
    onRetry: () => store.dispatch({ type: 'RETRY' }),
    embed: false,
    orchestratorEvents: orchestrator.events,
    getSortedPresets: () => orchestrator.getSortedPresets(),
    getTourSnapshot: () => orchestrator.getTourSnapshot(),
    getSortedHotspots: () => orchestrator.getSortedHotspots(),
    getHotspotsSnapshot: () => orchestrator.getHotspotsSnapshot(),
    getFloorplan: () => orchestrator.getFloorplan(),
    getFloorplanSnapshot: () => orchestrator.getFloorplanSnapshot(),
    getMeasurementsSnapshot: () => orchestrator.getMeasurementsSnapshot(),
    getRaycastAvailable: () => orchestrator.getRaycastAvailable(),
    getWalkSnapshot: () => orchestrator.getWalkSnapshot(),
    getWalkInputMode: () => orchestrator.getWalkInputMode(),
    getViewboxSnapshot: () => orchestrator.getViewboxSnapshot(),
    isCoachDismissed: () => isCoachDismissed(),
    resetCoachDismissed: () => resetCoachDismissed(),
    getCinematicActive: () => orchestrator.getCinematicActive(),
    getXRSnapshot: () => orchestrator.getXRSnapshot(),
    getDeviceCapabilities: () => orchestrator.getDeviceCapabilities(),
    getPostFxSnapshot: () => orchestrator.getPostFxSnapshot(),
    loadedCallbacks: {
      onScreenshot: () => {
        void orchestrator.takeScreenshot();
      },
      onReset: () => orchestrator.resetView(),
      onOverview: () => {
        void orchestrator.showOverview();
      },
      onFullscreenToggle: () => {
        void orchestrator.toggleFullscreen();
      },
      onPerfToggle: () => orchestrator.togglePerfHud(),
      onShare: () => {
        void orchestrator.shareUrl();
      },
      onApplyPreset: (presetId) => {
        void orchestrator.applyPreset(presetId);
      },
      onTourStart: () => orchestrator.startTour(),
      onTourPauseResume: () => {
        const snap = orchestrator.getTourSnapshot();
        if (snap.kind === 'paused') orchestrator.resumeTour();
        else orchestrator.pauseTour();
      },
      onHotspotOpen: (id) => orchestrator.openHotspot(id),
      onClose: () => orchestrator.closeHotspot(),
      onPrev: () => orchestrator.prevHotspot(),
      onNext: () => orchestrator.nextHotspot(),
      onFloorplanClose: () => orchestrator.closeFloorplan(),
      onFloorplanTeleport: (pixelXY) => orchestrator.teleportOnFloorplanPixel(pixelXY),
      onFloorplanImageError: () => orchestrator.reportFloorplanImageError(),
      onMeasureReset: () => orchestrator.resetMeasurements(),
      onMeasureSetUnits: (units) => orchestrator.setMeasurementUnits(units),
      onMeasureRemoveLast: () => orchestrator.removeLastMeasurePoint(),
      onOpenXR: () => orchestrator.openXRModal(),
      onXRClose: () => orchestrator.closeXRModal(),
      onEnterVR: () => orchestrator.enterXR('vr'),
      onEnterAR: () => orchestrator.enterXR('ar'),
      onWalkToggle: () => orchestrator.toggleWalk(),
      onMeasureToggle: () => orchestrator.toggleMeasure(),
      onNavigateToggle: () => orchestrator.toggleClickToMove(),
      onPostFxToggle: () => orchestrator.togglePostFx(),
      onUnavailableFeature: (reason) => orchestrator.notifyUnavailableFeature(reason),
      // WS5 PR3.3a: selectViewbox handler per viewbox switcher.
      onSelectViewbox: (arrayIdx) => {
        void orchestrator.selectViewbox(arrayIdx);
      },
      // WS5 PR3.3b: first-run coach handlers.
      // CTA "Entra" → toggleWalk acquisisce pointer-lock nello stesso
      // gesture click (browser user activation policy).
      onAcquirePointerLock: () => orchestrator.toggleWalk(),
      // Dismiss (CTA, close, ESC, auto-timeout) → persist localStorage.
      // Il re-render successivo (deriveCoachVisible) leggera' isCoachDismissed=true
      // e nascondera' il coach. NB: orchestrator non re-emette eventi qui;
      // il caller in main si affida al fatto che il prossimo dispatch
      // (es. walk-state al toggleWalk) triggera updateLoadedIfMounted.
      onDismissCoach: () => markCoachDismissed(),
      // WS5 PR3.3c: cinematic auto-tour toggle (toolbar button "film"
      // glyph). Orchestrator gestisce mutex + emit cinematic-state.
      onCinematicToggle: () => orchestrator.toggleCinematicTour(),
      // WS5 PR3.3c: help button. Renderer (wrappedLoadedCallbacks) wrappa
      // questo callback con reset coachDismissedThisSession + delega a
      // resetCoachDismissed (localStorage) + force re-render. Lasciamo
      // noop qui: il side-effect e' totalmente client-side.
      onHelpCoach: () => {},
    },
  });
  renderer.start();

  adapter.events.addEventListener('context-lost', () => {
    console.warn('[oniri-viewer] WebGL context lost — reload the page');
  });

  window.addEventListener(
    'pagehide',
    () => {
      // 4.M cleanup ordering: bodyDataset sub → renderer (which has
      // its own device-state listener) → orchestrator (which holds
      // the matcher subscription) → matcher itself.
      bodyDatasetUnsub();
      renderer.dispose();
      void orchestrator.dispose();
      deviceMatcher.dispose();
    },
    { once: true },
  );

  await orchestrator.start();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      void bootstrap();
    },
    { once: true },
  );
} else {
  void bootstrap();
}
